import React from 'react';
import './CandidateForm.scss';
import Input from 'components/ui/Input';
import TextArea from 'components/ui/TextArea';
import Checkbox from 'components/ui/Checkbox';
import AvatarInput from 'components/ui/AvatarInput';
import { showToast } from 'components/redesign/Toast';
import { ICandidateForm } from 'store/reducers/elections/types';
import MarkdownEditor from './MarkdownEditor';

export const PROGRAM_MIN_LENGTH = 1000;

interface FieldErrors {
    picture?: string;
    description?: string;
    program?: string;
    vk?: string;
    agreement?: string;
}

interface CandidateFormProps {
    form: ICandidateForm;
    setForm: React.Dispatch<React.SetStateAction<ICandidateForm>>;
    onValid: () => void;
    existingPictureUrl: string;
    editing: boolean;
}

function isHttpsLink(value: string): boolean {
    return value.startsWith('https://');
}
function validate(
    form: ICandidateForm,
    agreement: boolean,
    existingPictureUrl: string
): FieldErrors {
    const errors: FieldErrors = {};
    if (!form.picture && !existingPictureUrl) {
        errors.picture = 'Загрузи фотографию';
    }
    if (!form.description_text || !form.description_text.trim()) {
        errors.description = 'Расскажи немного о себе';
    }
    const program = form.program_text ? form.program_text.trim() : '';
    if (!program) {
        errors.program = 'Опиши предвыборную программу';
    } else if (program.length < PROGRAM_MIN_LENGTH) {
        errors.program = `Ещё ${
            PROGRAM_MIN_LENGTH - program.length
        } симв. до минимума в ${PROGRAM_MIN_LENGTH}`;
    }
    if (form.vk_nick && !isHttpsLink(form.vk_nick)) {
        errors.vk = 'Ссылка должна начинаться с https://';
    }
    if (!agreement) {
        errors.agreement = 'Нужно согласие на обработку данных';
    }
    return errors;
}

function FieldError({ text }: { text?: string }): React.ReactElement | null {
    if (!text) return null;
    return <p className="rd-candidate-form__error">{text}</p>;
}

FieldError.defaultProps = {
    text: undefined,
};
function CandidateForm({
    form,
    setForm,
    onValid,
    existingPictureUrl,
    editing,
}: CandidateFormProps): React.ReactElement {
    const { vk_nick, description_text, program_text } = form;

    const [agreement, setAgreement] = React.useState(editing);
    const [errors, setErrors] = React.useState<FieldErrors>({});
    const [touched, setTouched] = React.useState(false);
    React.useEffect(() => {
        if (!touched) return;
        setErrors(validate(form, agreement, existingPictureUrl));
    }, [form, agreement, existingPictureUrl, touched]);

    const onCheck = React.useCallback(() => {
        const found = validate(form, agreement, existingPictureUrl);
        setErrors(found);
        setTouched(true);
        if (Object.keys(found).length === 0) {
            onValid();
        } else {
            showToast('Заполни отмеченные поля', 'warning');
        }
    }, [form, agreement, existingPictureUrl, onValid]);

    return (
        <div className="rd-candidate-form">
            <p className="rd-candidate-form__hint">
                Эти данные увидят избиратели на странице выборов.
            </p>

            <div
                className={`rd-candidate-form__field${
                    errors.picture ? ' rd-candidate-form__field--error' : ''
                }`}
            >
                <AvatarInput
                    file={form.picture}
                    existingUrl={existingPictureUrl}
                    setFile={value =>
                        setForm(state => ({ ...state, picture: value }))
                    }
                />
                <FieldError text={errors.picture} />
            </div>

            <div className="rd-candidate-form__row">
                <Input
                    value={vk_nick}
                    onChange={e =>
                        setForm(state => ({
                            ...state,
                            vk_nick: e.target.value,
                        }))
                    }
                    placeholder="https://vk.com/durov"
                    label="Ссылка на профиль VK"
                    error={Boolean(errors.vk)}
                    setError={() =>
                        setErrors(state => ({ ...state, vk: undefined }))
                    }
                />
                <FieldError text={errors.vk} />
            </div>

            <div
                className={`rd-candidate-form__field${
                    errors.description ? ' rd-candidate-form__field--error' : ''
                }`}
            >
                <TextArea
                    value={description_text}
                    onChange={e =>
                        setForm(state => ({
                            ...state,
                            description_text: e.target.value,
                        }))
                    }
                    placeholder="Немного расскажи о себе: чем занимаешься
                    в университете, почему решил(а) баллотироваться"
                    label="О себе"
                />
                <FieldError text={errors.description} />
            </div>

            <div
                className={`rd-candidate-form__field${
                    errors.program ? ' rd-candidate-form__field--error' : ''
                }`}
            >
                <MarkdownEditor
                    value={program_text}
                    onChange={value =>
                        setForm(state => ({
                            ...state,
                            program_text: value,
                        }))
                    }
                    placeholder={`Подробно расскажи о своей предвыборной
                    программе (не менее ${PROGRAM_MIN_LENGTH} символов)`}
                />
                <FieldError text={errors.program} />
            </div>

            <div
                className={`rd-candidate-form__field${
                    errors.agreement ? ' rd-candidate-form__field--error' : ''
                }`}
            >
                <Checkbox
                    value={agreement}
                    onChange={value => setAgreement(value)}
                >
                    Я согласен(на) на обработку персональных данных
                </Checkbox>
                <FieldError text={errors.agreement} />
            </div>

            <button
                type="button"
                className="rd-btn rd-btn--green rd-candidate-form__submit"
                onClick={onCheck}
            >
                Проверить анкету
            </button>
        </div>
    );
}

export default React.memo(CandidateForm);
